import { useCallback, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Check,
  ShieldCheck,
  Users,
  Handshake,
  GraduationCap,
  TrendingDown,
  Sparkles,
  Quote,
  BadgeCheck,
  Clock,
  Star,
  MapPin,
  Layers,
  PlayCircle,
} from "lucide-react";

const VIDEO_HERO = "/group-discount.mp4";
const VIDEO_HERO_POSTER = "/group-discount-poster.jpg";
const TESTIMONIALS = [
  {
    src: "/testimonial-1.mp4",
    poster: "/testimonial-1-poster.jpg",
    testid: "testimonial-video-1",
  },
  {
    src: "/testimonial-2.mp4",
    poster: "/testimonial-2-poster.jpg",
    testid: "testimonial-video-2",
  },
];

const IMG_PROCEDURE =
  "https://images.pexels.com/photos/13207320/pexels-photo-13207320.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_COMMUNITY =
  "https://images.pexels.com/photos/7800675/pexels-photo-7800675.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_CLINIC =
  "https://images.pexels.com/photos/305567/pexels-photo-305567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_FOUNDER = "/founder-jonathan-theis.jpg";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const Kicker = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">
    {children}
  </p>
);

export default function LandingPage() {
  useEffect(() => {
    document.title = "FABG Canada — Founding Membership Application";
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        "content",
        "FABG Canada — Canada's First Full Arch Growth & Buying Network, built exclusively for private-practice implant & full arch dentists.",
      );
  }, []);

  const scrollToForm = useCallback(() => {
    const el = document.getElementById("application");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const features = [
    {
      icon: TrendingDown,
      title: "Better Pricing",
      body: "Leverage collective buying power to access preferred pricing from trusted vendors. As the Canadian network grows, we negotiate vendor relationships specifically for Canadian members.",
    },
    {
      icon: GraduationCap,
      title: "Full Arch Business Coaching",
      body: "Every member gets guidance from Jonathan Theis and the FABG leadership team \u2014 from growing production and case acceptance to profitability and digital workflows.",
    },
    {
      icon: Users,
      title: "Community of Growth-Oriented Owners",
      body: "Full arch dentistry can be isolating. Members collaborate daily in private communities, openly discussing what\u2019s working, what\u2019s not, labs, technology and more.",
    },
    {
      icon: ShieldCheck,
      title: "Trusted Vendors & Resources",
      body: "FABG continuously vets vendors, consultants, labs, software and marketing companies before recommending them \u2014 helping members find proven solutions faster.",
    },
  ];

  const pricing = [
    { label: "Implants", value: "$45", note: "as low as" },
    { label: "MUAs", value: "$22", note: "as low as" },
    { label: "Full Arch Zirconia", value: "$1,000", note: "incl. designs & revisions" },
    { label: "Implant Restorations", value: "$250", note: "with custom abutments" },
    { label: "Zirconia Crowns", value: "$59", note: "as low as" },
  ];

  const painPoints = [
    "Rising implant and restorative costs",
    "Increasing competition",
    "Expensive marketing with inconsistent results",
    "Difficulty scaling full arch treatment",
    "Limited access to trusted business resources",
    "Few opportunities to collaborate with full arch providers",
  ];

  const whoFor = [
    "Dentists currently placing implants",
    "Dentists performing All-on-X or full arch procedures",
    "Dentists looking to increase full arch production",
    "Practices looking to improve profitability",
    "Growth-oriented practice owners",
    "Dentists interested in digital workflows & emerging tech",
  ];

  const foundingBenefits = [
    "Complimentary membership while the Canadian network is built",
    "Direct input into future vendor partnerships",
    "Early access to negotiated pricing",
    "Priority access to educational resources",
    "Recognition as a Founding Member of FABG Canada",
    "Locked-in founding member benefits",
  ];

  const whyNow = [
    {
      icon: Sparkles,
      title: "Complimentary membership",
      body: "Join the founding cohort at no cost while we build the Canadian network.",
    },
    {
      icon: Layers,
      title: "Early vendor access",
      body: "Be first to access negotiated Canadian pricing and preferred vendor relationships.",
    },
    {
      icon: Star,
      title: "Shape the network",
      body: "Help shape vendor partnerships, educational content, webinars and community initiatives.",
    },
  ];

  const faqs = [
    {
      q: "Is there any obligation as a founding member?",
      a: "No. There is no obligation and no long-term commitment. We are simply looking for growth-minded Canadian dentists who want to help shape the future of the network.",
    },
    {
      q: "How does pricing access work?",
      a: "Members leverage the network\u2019s collective buying power to access preferred vendor pricing. The U.S. network already accesses significant savings, and we are building Canadian vendor relationships as the network grows.",
    },
    {
      q: "Is this only for full arch dentists?",
      a: "FABG Canada is built for private-practice implant and full arch dentists \u2014 whether you place 2 arches per month or 50. If you place implants or perform All-on-X / full arch procedures, it\u2019s a fit.",
    },
    {
      q: "Do I need to switch implant systems?",
      a: "No. You keep working the way you work today. The network is about better pricing, coaching, community and trusted resources \u2014 not changing your clinical preferences.",
    },
    {
      q: "When does the Canadian network launch?",
      a: "The Canadian founding cohort is forming now. Founding Members get in early and help build vendor partnerships and resources from the ground up.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header onApply={scrollToForm} />

      {/* HERO */}
      <section className="hero-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-24">
          <div className="lg:grid lg:grid-cols-12 lg:gap-10 lg:items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs font-medium text-slate-700">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  Canada&rsquo;s First Full Arch Growth &amp; Buying Network
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-[1.05]">
                  Lower Overhead.
                  <br />
                  <span className="text-primary">Grow More Arches.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                  A peer-led buying &amp; growth platform built exclusively for
                  private-practice implant &amp; full arch dentists. Learn from
                  doctors who&rsquo;ve already done it.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={scrollToForm}
                    data-testid="hero-apply-button"
                    className="h-12 px-6 rounded-xl text-base font-semibold shadow-sm hover:-translate-y-px hover:shadow-md transition-all duration-200 active:scale-[0.98]"
                  >
                    Apply for Founding Membership
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    onClick={scrollToForm}
                    variant="outline"
                    data-testid="hero-scroll-to-form-button"
                    className="h-12 px-6 rounded-xl text-base font-medium"
                  >
                    See member benefits
                  </Button>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-4 text-sm text-muted-foreground">
                  Complimentary membership while we build the Canadian network.
                  No obligation.
                </p>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="mt-7 flex flex-wrap gap-2">
                  {[
                    "Built by doctors, for private practice",
                    "Vendor-vetted pricing",
                    "Peer coaching + community",
                  ].map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs text-slate-700"
                    >
                      <BadgeCheck className="h-3.5 w-3.5 text-primary" />
                      {chip}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="mt-10 lg:mt-0 lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[320px] rounded-3xl bg-primary/5 p-2">
                  <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm">
                    <PlayCircle className="h-3.5 w-3.5" />
                    See how group pricing works
                  </span>
                  <video
                    className="w-full aspect-[9/16] object-cover rounded-2xl border border-border shadow-sm bg-black"
                    src={VIDEO_HERO}
                    poster={VIDEO_HERO_POSTER}
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    preload="metadata"
                    data-testid="hero-group-discount-video"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHY WE CREATED */}
      <section id="why" className="bg-white border-t border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>Why We Created FABG Canada</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              Canadian dentists face the same challenges we hear every day
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <Reveal className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {painPoints.map((p) => (
                  <div
                    key={p}
                    className="flex items-start gap-3 rounded-xl border border-border bg-secondary/50 p-4"
                  >
                    <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-destructive/10 text-destructive text-xs font-bold">
                      &times;
                    </span>
                    <span className="text-sm text-slate-700">{p}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="rounded-2xl border border-border bg-white shadow-sm p-6 relative overflow-hidden">
                <span className="absolute inset-x-0 top-0 h-0.5 bg-primary" />
                <p className="text-base text-slate-700 leading-relaxed">
                  The reality is that many private practices are trying to solve
                  these challenges <span className="font-semibold">alone</span>.
                </p>
                <p className="mt-4 text-lg font-semibold text-foreground">
                  FABG Canada was created to change that.
                </p>
                <Button
                  onClick={scrollToForm}
                  className="mt-6 h-11 rounded-xl font-semibold"
                  data-testid="why-apply-button"
                >
                  Apply for Founding Membership
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MORE THAN A BUYING GROUP */}
      <section id="benefits" className="section-accent border-t border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>More Than A Buying Group</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              A complete growth platform &mdash; not just discounts
            </h2>
            <p className="mt-3 max-w-2xl text-base text-muted-foreground">
              Most buying groups focus only on discounts. FABG Canada gives
              members access to four pillars of growth.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-white shadow-sm p-6 transition-shadow duration-200 hover:shadow-md">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent">
                    <f.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {f.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 flex justify-center">
              <Button
                onClick={scrollToForm}
                className="h-12 px-6 rounded-xl text-base font-semibold shadow-sm"
                data-testid="features-apply-button"
              >
                Apply for Founding Membership
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRICING EXAMPLES */}
      <section id="pricing" className="bg-white border-t border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <Reveal className="lg:col-span-5">
              <Kicker>Better Pricing</Kicker>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
                Make your savings tangible
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Examples from our existing U.S. network show what collective
                buying power makes possible. As the Canadian network grows,
                we&rsquo;ll build vendor relationships specifically for Canadian
                members.
              </p>
              <div className="mt-6 overflow-hidden rounded-2xl border border-border">
                <img
                  src={IMG_PROCEDURE}
                  alt="Dental implant components and procedure context"
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-7">
              <div
                data-testid="pricing-examples-block"
                className="rounded-2xl border border-border bg-secondary/40 shadow-sm relative overflow-hidden"
              >
                <span className="absolute inset-x-0 top-0 h-0.5 bg-primary" />
                <div className="p-6 sm:p-8">
                  {pricing.map((row, idx) => (
                    <div key={row.label}>
                      <div className="flex items-center justify-between py-4">
                        <div>
                          <p className="text-base font-medium text-foreground">
                            {row.label}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {row.note}
                          </p>
                        </div>
                        <p className="font-display text-2xl sm:text-3xl font-semibold tabular-nums text-primary">
                          {row.value}
                          <span className="ml-1 text-xs font-medium text-muted-foreground align-top">
                            USD
                          </span>
                        </p>
                      </div>
                      {idx < pricing.length - 1 && <Separator />}
                    </div>
                  ))}
                  <p className="mt-4 text-xs text-muted-foreground">
                    Examples shown in USD from the U.S. network. Pricing varies
                    by vendor and volume.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMMUNITY + VENDORS */}
      <section className="section-accent border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 space-y-16">
          {/* Community */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <Reveal>
              <div className="overflow-hidden rounded-3xl border border-border bg-primary/5 p-2">
                <img
                  src={IMG_COMMUNITY}
                  alt="Dental team collaborating and reviewing a case together"
                  className="w-full h-64 sm:h-80 object-cover rounded-2xl"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <Kicker>Community of Growth-Oriented Owners</Kicker>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
                Learn from doctors facing the same challenges you are
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Full arch dentistry can be isolating. FABG members collaborate
                daily through private communities where doctors openly discuss
                what&rsquo;s working and what&rsquo;s not.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                {[
                  "Marketing strategies",
                  "Labs & workflows",
                  "New technology & AI tools",
                  "Team management",
                  "Clinical challenges",
                  "Business challenges",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="h-4 w-4 flex-none text-primary" />
                    {t}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Vendors */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <Reveal className="lg:order-2">
              <div className="overflow-hidden rounded-3xl border border-border bg-primary/5 p-2">
                <img
                  src={IMG_CLINIC}
                  alt="Clean modern dental clinic interior"
                  className="w-full h-64 sm:h-80 object-cover rounded-2xl"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal delay={0.05} className="lg:order-1">
              <Kicker>Trusted Vendors &amp; Business Resources</Kicker>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
                Avoid costly mistakes and wasted time
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                FABG continuously vets vendors, consultants, labs, software
                providers, marketing companies and operational resources before
                recommending them to members.
              </p>
              <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-border bg-white p-4 shadow-sm">
                <Handshake className="h-6 w-6 text-primary" />
                <p className="text-sm font-medium text-foreground">
                  Our goal is simple: help members find proven solutions faster.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="bg-white border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>Who Is FABG Canada For?</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              Built for growth-minded implant &amp; full arch dentists
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 rounded-2xl border border-border bg-secondary/40 p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whoFor.map((w) => (
                  <div key={w} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-sm sm:text-base text-slate-700">{w}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-base text-slate-700">
                Whether you&rsquo;re placing 2 arches per month or 50, the goal
                is the same:{" "}
                <span className="font-semibold text-foreground">
                  help you grow smarter and more profitably.
                </span>
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex justify-center">
              <Button
                onClick={scrollToForm}
                className="h-12 px-6 rounded-xl text-base font-semibold shadow-sm"
                data-testid="whofor-apply-button"
              >
                Apply for Founding Membership
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOUNDING MEMBER OPPORTUNITY */}
      <section className="border-t border-border bg-foreground">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <Reveal className="lg:col-span-5">
              <Badge className="rounded-full bg-primary text-primary-foreground hover:bg-primary border-0">
                <Clock className="mr-1.5 h-3.5 w-3.5" />
                Founding cohort forming now
              </Badge>
              <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
                Founding Member Opportunity
              </h2>
              <p className="mt-4 text-base text-slate-300 leading-relaxed">
                We are currently selecting a limited number of Founding Members
                for FABG Canada. There is no obligation and no long-term
                commitment &mdash; we&rsquo;re looking for growth-minded Canadian
                dentists who want to help shape the future of the network.
              </p>
              <Button
                onClick={scrollToForm}
                className="mt-7 h-12 px-6 rounded-xl text-base font-semibold shadow-sm"
                data-testid="founding-apply-button"
              >
                Secure Your Founding Status
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-7">
              <div
                data-testid="founding-member-benefits"
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8"
              >
                <p className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                  Founding Members receive
                </p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {foundingBenefits.map((b) => (
                    <div key={b} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-4 w-4" />
                      </span>
                      <span className="text-sm text-slate-100">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY JOIN NOW */}
      <section className="bg-white border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>Why Join Now?</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              The biggest advantage goes to those who join early
            </h2>
            <p className="mt-3 max-w-2xl text-base text-muted-foreground">
              This is your opportunity to help build Canada&rsquo;s premier full
              arch growth network from the ground up.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {whyNow.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-white shadow-sm p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent">
                    <c.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEET JONATHAN THEIS */}
      <section
        data-testid="founder-bio-section"
        className="section-accent border-t border-border"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <Reveal className="lg:col-span-4">
              <div className="rounded-2xl border border-border bg-white shadow-sm p-6 text-center">
                <div className="mx-auto h-32 w-32 overflow-hidden rounded-full border-4 border-accent shadow-sm">
                  <img
                    src={IMG_FOUNDER}
                    alt="Dr. Jonathan Theis, Founder of the Full Arch Buying Group"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="mt-5 text-lg font-semibold text-foreground">
                  Dr. Jonathan Theis
                </p>
                <p className="text-sm text-muted-foreground">
                  Founder, Full Arch Buying Group
                </p>
                <div className="mt-4 flex justify-center gap-1.5 text-primary">
                  {[0, 1, 2].map((n) => (
                    <Sparkles key={n} className="h-4 w-4" />
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-8">
              <Kicker>Meet Jonathan Theis</Kicker>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
                Built by a full arch dentist, for full arch dentists
              </h2>
              <div className="mt-5 rounded-2xl border border-border bg-white shadow-sm p-6 relative">
                <Quote className="h-7 w-7 text-primary/30" />
                <p className="mt-2 text-base text-slate-700 leading-relaxed">
                  Dr. Jonathan Theis is a full arch dentist, practice owner,
                  consultant, and founder of the Full Arch Buying Group. After
                  years of building and scaling implant practices, Jonathan
                  created FABG to help private practice owners gain access to
                  the resources, relationships, and guidance typically reserved
                  for larger organizations.
                </p>
                <p className="mt-4 text-base text-slate-700 leading-relaxed">
                  Today, FABG members across North America leverage the network
                  to improve profitability, grow full arch production, and stay
                  ahead of industry trends.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        id="testimonials"
        data-testid="testimonials-section"
        className="section-accent border-t border-border scroll-mt-20"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>What Members Are Saying</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              Hear directly from doctors in the network
            </h2>
            <p className="mt-3 max-w-2xl text-base text-muted-foreground">
              Real members sharing how the Full Arch Buying Group has impacted
              their practices.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((v, i) => (
              <Reveal key={v.src} delay={i * 0.05}>
                <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
                  <video
                    className="w-full aspect-video bg-black object-contain"
                    src={v.src}
                    poster={v.poster}
                    controls
                    playsInline
                    preload="metadata"
                    data-testid={v.testid}
                  />
                  <div className="flex items-center gap-2 px-4 py-3">
                    <Quote className="h-4 w-4 flex-none text-primary/50" />
                    <span className="text-sm font-medium text-slate-700">
                      FABG member testimonial
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 flex justify-center">
              <Button
                onClick={scrollToForm}
                className="h-12 px-6 rounded-xl text-base font-semibold shadow-sm"
                data-testid="testimonials-apply-button"
              >
                Apply for Founding Membership
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section
        id="application"
        data-testid="application-section"
        className="bg-white border-t border-border scroll-mt-20"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <Reveal className="lg:col-span-5">
              <Kicker>Become A Founding Member</Kicker>
              <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                Apply for Founding Membership
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Join the doctors helping build Canada&rsquo;s first dedicated
                Full Arch Growth &amp; Buying Network. Apply today and secure
                your Founding Member status.
              </p>
              <div className="mt-6 space-y-3">
                {[
                  "Complimentary while we build the Canadian network",
                  "No obligation, no long-term commitment",
                  "Locked-in founding member benefits",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-sm text-slate-700">{t}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="rounded-2xl border border-border bg-white shadow-md p-5 sm:p-7">
                <h3 className="text-xl font-semibold text-foreground">
                  Tell us about your practice
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Complimentary while we build the Canadian network. No obligation.
                </p>
                <div className="mt-5">
                  <LeadForm />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="section-accent border-t border-border scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>Common Questions</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              Frequently asked questions
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <Accordion type="single" collapsible className="mt-8">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="border border-border rounded-xl bg-white mb-3 px-4"
                >
                  <AccordionTrigger className="text-left text-sm sm:text-base font-medium hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 flex justify-center">
              <Button
                onClick={scrollToForm}
                className="h-12 px-6 rounded-xl text-base font-semibold shadow-sm"
                data-testid="faq-apply-button"
              >
                Apply for Founding Membership
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
